import * as cookie from 'cookie'
import { IncomingMessage } from 'http'
import { NextApiRequest } from 'next'

export const AUTH_COOKIES = {
  HAS_RUN_SILENT_LOGIN_STATE: 'RP_SILENT_AUTH',
  HAS_SILENT_LOGIN_IN_PROGRESS_STATE: 'RP_SILENT_AUTH_IN_PROGRESS',
  USER_NOT_LOGGED_IN: 'RP_USER_NOT_LOGGED_IN',
  AUTH_ERROR_CODE: 'RP_AUTH_ERROR_CODE',
  AUTH0_SESSION_COOKIE_NAME: 'appSession',
} as const

export const AUTH_TRANSACTION_COOKIE_PREFIX = '__txn_'

export const AUTH_SESSION_COOKIE_PREFIXES = [
  'appSession',
  '__session_',
  AUTH_TRANSACTION_COOKIE_PREFIX,
]

// Used after a successful silent login callback: clears the legacy server session
export const AUTH_SESSION_COOKIE_PREFIXES_TO_CLEAR_ON_CALLBACK = [
  'appSession',
  AUTH_TRANSACTION_COOKIE_PREFIX,
]

type SerializeAuth0ClearSessionCookiesOptions = {
  includeTransactionCookies?: boolean
}

export const serializeAuth0ClearSessionCookies = (
  cookies: Partial<Record<string, string>>,
  { includeTransactionCookies = true }: SerializeAuth0ClearSessionCookiesOptions = {}
): string[] => {
  const prefixesToClear = includeTransactionCookies
    ? AUTH_SESSION_COOKIE_PREFIXES
    : AUTH_SESSION_COOKIE_PREFIXES.filter((prefix) => prefix !== AUTH_TRANSACTION_COOKIE_PREFIX)

  return Object.keys(cookies)
    .filter((key) => prefixesToClear.some((prefix) => key.startsWith(prefix)))
    .map((key) => cookie.serialize(key, '', { maxAge: 0, path: '/' }))
}

export const SILENT_LOGIN_FLAG_EXPIRY_TIME_IN_SECONDS = 300
const SILENT_LOGIN_IN_PROGRESS_EXPIRY_TIME_IN_SECONDS = 120

export const serializeSilentLoginCookie = (enabled: boolean): string =>
  cookie.serialize(AUTH_COOKIES.HAS_RUN_SILENT_LOGIN_STATE, String(enabled), {
    path: '/',
    maxAge: enabled ? SILENT_LOGIN_FLAG_EXPIRY_TIME_IN_SECONDS : 0,
  })

export const serializeSilentLoginInProgressCookie = (enabled: boolean): string =>
  cookie.serialize(AUTH_COOKIES.HAS_SILENT_LOGIN_IN_PROGRESS_STATE, String(enabled), {
    path: '/',
    maxAge: enabled ? SILENT_LOGIN_IN_PROGRESS_EXPIRY_TIME_IN_SECONDS : 0,
  })

export type RequestCookies = Partial<Record<string, string>>

// Check if IncomingMessage is NextApiRequest
const requestHasCookies = (req: IncomingMessage | NextApiRequest): req is NextApiRequest => {
  return (req as NextApiRequest).cookies !== undefined
}

const parseCookieString = (cookieString?: string) => cookie.parse(cookieString ?? '')

const getCookiesFromRequest = <R extends IncomingMessage>(req: R): RequestCookies => {
  return requestHasCookies(req) ? req.cookies : parseCookieString(req.headers.cookie)
}

export const getCookieBooleanValue = <R extends IncomingMessage>(req: R, key: string) => {
  const cookies = getCookiesFromRequest(req)
  return cookies[key] === 'true'
}
